<!-- bwell feedback modal fixed -->
<script setup lang="ts">
import BButton from '@/components/BButton.vue';
import SpeechBubble from '@/components/SpeechBubble.vue';
import {
  IonContent,
  IonTitle,
  IonToolbar,
  modalController,
  IonProgressBar,
  IonIcon,
  IonHeader,
  toastController,
} from '@ionic/vue';
import { useStore } from '@/stores/main';
import monsterUrl from '/assets/monster.png';
import { computed, ref, onMounted } from 'vue';
import { checkmarkCircle, helpCircle, alertCircle } from 'ionicons/icons';

type FeedbackOption = {
  text: string;
  value: number | string;
};

type FeedbackQuestion = {
  name: string;
  text: string;
  options: FeedbackOption[];
};

const store = useStore();
const questions = computed<FeedbackQuestion[]>(() => {
  return (store.feedbackQuestions || []) as FeedbackQuestion[];
});

const answers = ref<Record<string, number>>({});
const showThankYou = ref(false);
const isSaving = ref(false);
const hasError = ref(false);
const scaleValues = [1, 2, 3, 4, 5];

const questionTitleOverrides: Record<string, string> = {
  'feedback-meaningful': 'Jag tyckte det var STIMULERANDE att göra Bwell.',
  'feedback-responsibility': 'Jag har fått ÖKAD KUNSKAP om hur jag kan förbättra mina levnadsvanor.',
  'feedback-stimulating': 'Jag är MOTIVERAD att genomföra min livsstilsplan.',
};

// Ensure we have questions loaded
onMounted(async () => {
  try {
    await store.fetchFeedbackQuestions();
  } catch (error) {
    console.error('Failed to load feedback questions:', error);
    presentErrorToast('Failed to load feedback questions');
  }
});

// Calculate progress percentage
const progressPercentage = computed(() => {
  if (!questions.value || questions.value.length === 0) return 0;
  return (Object.keys(answers.value).length / questions.value.length) * 100;
});

// Check if all questions are answered
const isDone = computed(() => {
  if (!questions.value || questions.value.length === 0) return false;

  return (
    Object.values(answers.value).filter(v => v !== null && v !== undefined).length ===
    questions.value.length
  );
});

// Present error toast
async function presentErrorToast(message: string) {
  const toast = await toastController.create({
    message: message,
    duration: 3000,
    position: 'bottom',
    color: 'danger',
    icon: alertCircle,
  });
  toast.present();
}

// Submit answers and close modal
// Submit answers and close modal
// BwellFeedbackModal.vue
async function done() {
  if (isSaving.value) return; // Prevent multiple submissions
  
  isSaving.value = true;
  hasError.value = false;
  
  try {
    if (!store.profile?.id) {
      console.error('No active profile found');
      throw new Error('No active profile');
    }
    
    // Convert values to strings to match the backend expectation
    const stringAnswers: Record<string, string> = {};
    Object.keys(answers.value).forEach(key => {
      stringAnswers[key] = String(answers.value[key]);
    });
    
    console.log('Preparing to send feedback answers:', stringAnswers);
    
    const result = await store.saveFeedback(stringAnswers);
    console.log('Feedback save result:', result);
    
    // Handle 403 Forbidden - feedback already submitted
    if (result && result.error === 'Forbidden') {
      console.log('Feedback already submitted for this profile, showing thank you anyway');
      showThankYou.value = true;
      setTimeout(() => {
        modalController.dismiss({ success: false, alreadySubmitted: true }, 'submit');
      }, 2000);
      return;
    }
    
    if (!result || result.success === false) {
      if (result && result.details) {
        console.error('Validation errors:', result.details);
        throw new Error(`Validation error: ${JSON.stringify(result.details)}`);
      } else {
        throw new Error(result?.error || 'Failed to save feedback');
      }
    }
    
    // Show thank you message on success
    showThankYou.value = true;
    
    // Dismiss with success status
    setTimeout(() => {
      modalController.dismiss({ success: true, answers: stringAnswers }, 'submit');
    }, 2000);
  } catch (error) {
    console.error('Failed to save feedback:', error);
    hasError.value = true;
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    presentErrorToast('Failed to save feedback. Please try again.');
    // Dismiss with error status
    setTimeout(() => {
      modalController.dismiss({ success: false, error: errorMessage }, 'submit');
    }, 2000);
  } finally {
    isSaving.value = false;
  }
}

// Handle answer selection
function selectAnswer(questionName: string, value: number) {
  answers.value[questionName] = value;
}

function getQuestionLetter(index: number) {
  return String.fromCharCode(65 + index);
}

function getDisplayQuestionText(question: FeedbackQuestion) {
  return questionTitleOverrides[question.name] || question.text;
}

// Animation for monster
const isMonsterAnimated = ref(false);
setTimeout(() => {
  isMonsterAnimated.value = true;
}, 300);
</script>
<template>
  <!-- Use IonHeader outside IonContent for fixed header -->
  <ion-header>
    <ion-toolbar class="feedback-toolbar">
      <ion-title class="feedback-title">Avslutande frågor</ion-title>
      <ion-progress-bar
        :value="progressPercentage / 100"
        color="success"
        class="progress-bar"
      ></ion-progress-bar>
    </ion-toolbar>
  </ion-header>

  <!-- Let IonContent handle the scrolling -->
  <ion-content :fullscreen="true" class="ion-padding">
    <!-- Thank you overlay shown when submitted -->
    <div v-if="showThankYou" class="thank-you-overlay">
      <div class="thank-you-content">
        <ion-icon :icon="checkmarkCircle" class="thank-you-icon"></ion-icon>
        <h2>Tack för din feedback!</h2>
        <p>Dina svar hjälper oss att förbättra Bwell.</p>
      </div>
    </div>

    <div class="feedback-layout">
      <!-- Left column with monster and speech bubble -->
      <div class="monster-section">
        <div
          class="flex justify-center flex-col-reverse md:flex-row items-center relative w-[40%] md:w-[80%] mx-auto"
        >
          <img 
            class="monster md:mt-0" 
            :class="{ 'animated': isMonsterAnimated }"
            :src="monsterUrl" 
            alt="Monster character"
          />

          <div class="relative flex justify-center items-center">
            <speech-bubble
              color="green"
              class="w-[100%] md:rotate-[70deg] -scale-x-100 -z-5"
            />

            <div
              class="absolute md:pl-5 lg:text-[15px] text-[min(5vw,1.15rem)] text-center w-[80%] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            >
             Nu bara 3 avslutande frågor om vad du tycker om Bwell-arbetet.
            </div>
          </div>
        </div>
      </div>

      <!-- Right column with questions - no need for separate scrolling -->
      <div class="questions-section">
        <h2 class="questions-title">Klicka på det värde 1-5 som passar bäst in på din upplevelse av Bwell</h2>
        <p class="questions-subtitle">(OBS 5 = högsta betyg)</p>

        <div v-if="!questions || !questions.length" class="no-questions">
          <ion-icon :icon="helpCircle" class="no-questions-icon"></ion-icon>
          <p>Inga frågor tillgängliga just nu.</p>
        </div>

        <ul v-else class="questions-list">
          <li
            v-for="(question, index) in questions"
            :key="index"
            class="question-item"
            :class="{
              answered: answers[question.name] !== undefined,
              completed: answers[question.name] !== undefined,
            }"
          >
            <div class="question-header">
              <div class="question-number">{{ getQuestionLetter(index) }}</div>
              <div class="question-text">{{ getDisplayQuestionText(question) }}</div>
            </div>

            <div class="scale-options" role="radiogroup" :aria-label="question.text">
              <button
                v-for="scale in scaleValues"
                :key="scale"
                type="button"
                class="scale-option"
                :class="{ selected: answers[question.name] === scale }"
                @click="selectAnswer(question.name, scale)"
              >
                {{ scale }}
              </button>
            </div>

            <div class="scale-hints">
              <span>Låg</span>
              <span>Hög</span>
            </div>

            <div
              v-if="answers[question.name] !== undefined"
              class="answer-confirmation"
            >
              <ion-icon :icon="checkmarkCircle" class="check-icon"></ion-icon>
              <span>Svar registrerat</span>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <!-- Fixed bottom submit button -->
    <div class="submit-container">
      <div v-if="hasError" class="error-message">
        <ion-icon :icon="alertCircle" class="error-icon"></ion-icon>
        <span>Det gick inte att skicka svaren. Försök igen.</span>
      </div>
      <div class="completion-hint" v-else-if="!isDone">
        Vänligen besvara alla frågor för att fortsätta
      </div>
      <b-button
        :disabled="!isDone || isSaving"
        @click="done"
        class="submit-button"
        :class="{ 'button-ready': isDone && !isSaving }"
      >
        {{ isSaving ? 'Skickar...' : 'Skicka svar' }}
      </b-button>
    </div>
  </ion-content>
</template>

<style scoped>
/* Base styles */
ion-content {
  --background: #f0f9ea;
  --padding-bottom: 120px; /* Extra padding to ensure content doesn't hide behind submit button */
}

.feedback-toolbar {
  --background: #4ca154;
  --color: white;
  --border-color: transparent;
  --min-height: 64px;
  padding: 0.5rem 0;
  position: relative;
}

.feedback-title {
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
  padding-bottom: 0.5rem;
}

.progress-bar {
  width: 80%;
  margin: 0 auto;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
}

/* Main layout */
.feedback-layout {
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  margin: 0 auto;
  gap: 1.5rem;
}

/* Monster section */
.monster-section {
  padding: 0.5rem;
}

.monster {
  width: 50%;
  max-width: 160px;
  transform: translateY(20px) scale(0.95);
  opacity: 0;
  transition:
    transform 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275),
    opacity 0.6s ease;
}

.monster.animated {
  transform: translateY(0) scale(1);
  opacity: 1;
}

/* Questions section */
.questions-section {
  padding: 0.5rem;
}

.questions-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2e6c34;
  margin: 0;
  text-align: center;
}

.questions-subtitle {
  text-align: center;
  margin: 0.25rem 0 1.5rem;
  color: #2e6c34;
  font-weight: 600;
}

.no-questions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.no-questions-icon {
  font-size: 3rem;
  color: #ccc;
  margin-bottom: 1rem;
}

.questions-list {
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
}

.question-item {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
  padding: 1.25rem;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
}

.question-item.completed {
  border-left: 4px solid #4ca154;
}

.question-header {
  display: flex;
  margin-bottom: 1rem;
  align-items: flex-start;
}

.question-number {
  background-color: #4ca154;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.question-text {
  font-size: 1rem;
  line-height: 1.4;
  padding-top: 0.2rem;
}

.scale-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  margin-top: 0.75rem;
  width: 100%;
}

.scale-option {
  width: clamp(38px, 7vw, 46px);
  height: clamp(38px, 7vw, 46px);
  border-radius: 999px;
  border: 2px solid #4ca154;
  background: transparent;
  color: #2e6c34;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 0 0 auto;
}

.scale-option:hover {
  background: #eef8e9;
}

.scale-option.selected {
  background: #4ca154;
  color: #fff;
}

.scale-hints {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #77836f;
  margin-top: 0.4rem;
  padding: 0;
}

.answer-confirmation {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #4ca154;
  margin-top: 0.75rem;
}

.check-icon {
  font-size: 1.1rem;
  margin-right: 0.4rem;
}

/* Submit button section */
.submit-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(240, 249, 234, 0.95);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  z-index: 1000;
}

.completion-hint {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.75rem;
  text-align: center;
}

.error-message {
  font-size: 0.9rem;
  color: #d32f2f;
  margin-bottom: 0.75rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-icon {
  font-size: 1.2rem;
  margin-right: 0.4rem;
}

.submit-button {
  min-width: 180px;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.button-ready {
  box-shadow: 0 4px 12px rgba(76, 161, 84, 0.25);
}

.button-ready:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(76, 161, 84, 0.3);
}

/* Thank you overlay */
.thank-you-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.95);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.5s ease forwards;
}

.thank-you-content {
  text-align: center;
  padding: 2rem;
  max-width: 400px;
}

.thank-you-icon {
  font-size: 5rem;
  color: #4ca154;
  margin-bottom: 1rem;
  animation: pulse 1.5s infinite;
}

.thank-you-content h2 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #2e6c34;
  margin: 0 0 1rem 0;
}

.thank-you-content p {
  font-size: 1.1rem;
  color: #666;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* Responsive styles */
@media (min-width: 768px) {
  .feedback-layout {
    flex-direction: row;
    padding: 1rem 2rem;
  }

  .monster-section {
    width: 30%;
    padding: 1.5rem;
  }

  .questions-section {
    width: 70%;
    padding: 1.5rem;
  }

  .question-item {
    padding: 1.5rem;
  }

  .submit-container {
    padding: 1.5rem;
  }
}

@media (min-width: 1024px) {
  .monster-section {
    width: 25%;
  }

  .questions-section {
    width: 75%;
  }

  .questions-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
  }
}
</style>